import { useState, useEffect, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import type { VehiclePricing, VehiclePricingAddon, VehiclePricingTier } from '@limo/database'
import { TieredPricingEditor } from './TieredPricingEditor'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import {
  MapPin,
  Clock,
  Loader2,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Check,
  HelpCircle,
} from 'lucide-react'
import {
  PRICING_TYPE_LABELS,
  ADDON_PRICE_TYPE_LABELS,
} from '@/schemas/vehicle'

// Schema for inline pricing form
const inlinePricingSchema = z.object({
  base_price: z.coerce.number().min(0, 'Base price must be 0 or greater'),
  price_per_hour: z.coerce.number().min(0).optional().nullable(),
  price_per_mile: z.coerce.number().min(0).optional().nullable(),
  minimum_hours: z.coerce.number().min(0).optional().nullable(),
  minimum_miles: z.coerce.number().min(0).optional().nullable(),
  service_fee: z.coerce.number().min(0).optional().nullable(),
  use_tiered_pricing: z.boolean().default(false),
})

type InlinePricingFormData = z.infer<typeof inlinePricingSchema>

interface PricingTypeCardProps {
  pricingType: 'point_to_point' | 'hourly'
  vehicleId: string
  accountId: string
  pricing: (VehiclePricing & {
    vehicle_pricing_addons: VehiclePricingAddon[]
    vehicle_pricing_tiers?: VehiclePricingTier[]
  }) | null
  onPricingChange: () => void
  onEditAddon: (pricingId: string, addonId: string | null) => void
  onDeleteAddon: (addonId: string) => void
}

export function PricingTypeCard({
  pricingType,
  vehicleId,
  accountId,
  pricing,
  onPricingChange,
  onEditAddon,
  onDeleteAddon,
}: PricingTypeCardProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [showDisableConfirm, setShowDisableConfirm] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null)

  const isEnabled = pricing?.is_active ?? false
  const hasAddons = (pricing?.vehicle_pricing_addons?.length ?? 0) > 0

  const Icon = pricingType === 'point_to_point' ? MapPin : Clock
  const typeLabel = PRICING_TYPE_LABELS[pricingType]
  const typeDescription =
    pricingType === 'point_to_point'
      ? 'Pricing for one-way trips from pickup to dropoff location. Charges are based on base fare plus per-mile rate.'
      : 'Pricing for trips billed by duration. Ideal for events, tours, or trips with multiple stops.'
  const disabledMessage =
    pricingType === 'point_to_point'
      ? 'This vehicle is not available for point-to-point bookings.'
      : 'This vehicle is not available for hourly bookings.'

  const form = useForm<InlinePricingFormData>({
    resolver: zodResolver(inlinePricingSchema),
    defaultValues: {
      base_price: 0,
      price_per_hour: null,
      price_per_mile: null,
      minimum_hours: null,
      minimum_miles: null,
      service_fee: null,
      use_tiered_pricing: false,
    },
  })

  // Sync form values with pricing data
  useEffect(() => {
    if (pricing) {
      form.reset({
        base_price: Number(pricing.base_price) || 0,
        price_per_hour: pricing.price_per_hour ? Number(pricing.price_per_hour) : null,
        price_per_mile: pricing.price_per_mile ? Number(pricing.price_per_mile) : null,
        minimum_hours: pricing.minimum_hours ? Number(pricing.minimum_hours) : null,
        minimum_miles: pricing.minimum_miles ? Number(pricing.minimum_miles) : null,
        service_fee: pricing.service_fee ? Number(pricing.service_fee) : null,
        use_tiered_pricing: pricing.use_tiered_pricing ?? false,
      })
    }
  }, [pricing, form])

  // Create pricing mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('vehicle_pricing').insert({
        vehicle_id: vehicleId,
        account_id: accountId,
        pricing_type: pricingType,
        base_price: 0,
        is_active: true,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-detail', vehicleId] })
      onPricingChange()
      toast({ title: `${typeLabel} pricing enabled` })
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  // Toggle active state mutation
  const toggleMutation = useMutation({
    mutationFn: async (active: boolean) => {
      if (!pricing) throw new Error('No pricing to toggle')
      const { error } = await supabase
        .from('vehicle_pricing')
        .update({ is_active: active })
        .eq('id', pricing.id)
      if (error) throw error
    },
    onSuccess: (_, active) => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-detail', vehicleId] })
      onPricingChange()
      toast({ title: active ? `${typeLabel} pricing enabled` : `${typeLabel} pricing disabled` })
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  // Update pricing mutation
  const updateMutation = useMutation({
    mutationFn: async (data: InlinePricingFormData) => {
      if (!pricing) throw new Error('No pricing to update')
      const { error } = await supabase
        .from('vehicle_pricing')
        .update({
          base_price: data.base_price,
          price_per_hour: data.price_per_hour || null,
          price_per_mile: data.price_per_mile || null,
          minimum_hours: data.minimum_hours || null,
          minimum_miles: data.minimum_miles || null,
          service_fee: data.service_fee || null,
          use_tiered_pricing: data.use_tiered_pricing ?? false,
        })
        .eq('id', pricing.id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-detail', vehicleId] })
      setIsSaving(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 2000)
    },
    onError: (error) => {
      setIsSaving(false)
      toast({ title: 'Error saving', description: error.message, variant: 'destructive' })
    },
  })

  // Debounced save function
  const debouncedSave = useCallback(
    (data: InlinePricingFormData) => {
      if (saveTimeout) {
        clearTimeout(saveTimeout)
      }
      setIsSaving(true)
      const timeout = setTimeout(() => {
        updateMutation.mutate(data)
      }, 500)
      setSaveTimeout(timeout)
    },
    [saveTimeout, updateMutation]
  )

  // Watch form changes and auto-save
  useEffect(() => {
    const subscription = form.watch((value) => {
      if (pricing && isEnabled) {
        const isValid = form.formState.isValid
        if (isValid) {
          debouncedSave(value as InlinePricingFormData)
        }
      }
    })
    return () => subscription.unsubscribe()
  }, [form, pricing, isEnabled, debouncedSave])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout)
      }
    }
  }, [saveTimeout])

  const handleToggle = (checked: boolean) => {
    if (checked) {
      // Enable
      if (pricing) {
        toggleMutation.mutate(true)
      } else {
        createMutation.mutate()
      }
    } else {
      // Disable - show confirmation if has addons
      if (hasAddons) {
        setShowDisableConfirm(true)
      } else {
        toggleMutation.mutate(false)
      }
    }
  }

  const confirmDisable = () => {
    toggleMutation.mutate(false)
    setShowDisableConfirm(false)
  }

  const formatAddonPrice = (price: number, priceType: string) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price)
    const typeLabel =
      ADDON_PRICE_TYPE_LABELS[priceType as keyof typeof ADDON_PRICE_TYPE_LABELS]
    return priceType === 'flat' ? formatted : `${formatted} ${typeLabel.toLowerCase()}`
  }

  const isToggling = createMutation.isPending || toggleMutation.isPending

  return (
    <TooltipProvider>
      <Card className={`transition-opacity duration-200 ${!isEnabled ? 'opacity-75' : ''}`}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base font-semibold">{typeLabel}</CardTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 cursor-help text-muted-foreground/70" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p>{typeDescription}</p>
              </TooltipContent>
            </Tooltip>
            {isSaving && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            {saveSuccess && !isSaving && (
              <Check className="h-4 w-4 text-green-500 animate-in fade-in duration-200" />
            )}
          </div>
          <div className="flex items-center gap-2">
            {isToggling && <Loader2 className="h-4 w-4 animate-spin" />}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={handleToggle}
                    disabled={isToggling}
                    aria-label={`${isEnabled ? 'Disable' : 'Enable'} ${typeLabel} pricing`}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>{isEnabled ? 'Disable' : 'Enable'} {typeLabel.toLowerCase()} pricing</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </CardHeader>
        <CardContent>
          {isEnabled ? (
            <Form {...form}>
              <form className="space-y-4">
                {/* Form fields grid */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {/* Base Price - only for point-to-point (hourly uses hours × rate only) */}
                  {pricingType === 'point_to_point' && (
                    <FormField
                      control={form.control}
                      name="base_price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Base Price</FormLabel>
                          <FormControl>
                            <CurrencyInput
                              placeholder="0.00"
                              value={field.value}
                              onChange={(value) => field.onChange(value ?? 0)}
                            />
                          </FormControl>
                          <FormDescription className="text-xs">
                            Base fare for the trip
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Point-to-Point specific fields */}
                  {pricingType === 'point_to_point' && !form.watch('use_tiered_pricing') && (
                    <>
                      <FormField
                        control={form.control}
                        name="minimum_miles"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Miles Included</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={0}
                                step="0.1"
                                placeholder="10"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value ? Number(e.target.value) : null
                                  )
                                }
                              />
                            </FormControl>
                            <FormDescription className="text-xs">
                              Included in base price
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="price_per_mile"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Price Per Mile</FormLabel>
                            <FormControl>
                              <CurrencyInput
                                placeholder="3.00"
                                value={field.value}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">
                              For miles beyond included
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}

                  {/* Hourly specific fields */}
                  {pricingType === 'hourly' && (
                    <>
                      <FormField
                        control={form.control}
                        name="price_per_hour"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Price Per Hour</FormLabel>
                            <FormControl>
                              <CurrencyInput
                                placeholder="65.00"
                                value={field.value}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">
                              Hourly rate
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="minimum_hours"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Minimum Hours</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={0}
                                step="0.5"
                                placeholder="2"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value ? Number(e.target.value) : null
                                  )
                                }
                              />
                            </FormControl>
                            <FormDescription className="text-xs">
                              Minimum booking duration
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}

                  {/* Service Fee - always shown */}
                  <FormField
                    control={form.control}
                    name="service_fee"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Service Fee</FormLabel>
                        <FormControl>
                          <CurrencyInput
                            placeholder="0.00"
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          Optional flat fee per booking
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Tiered Pricing Toggle and Editor - only for point-to-point */}
                {pricingType === 'point_to_point' && pricing && (
                  <div className="border-t pt-4">
                    <FormField
                      control={form.control}
                      name="use_tiered_pricing"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                          <div className="space-y-0.5">
                            <FormLabel>Use Tiered Mileage Pricing</FormLabel>
                            <FormDescription className="text-xs">
                              Configure different rates for different mileage ranges
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {form.watch('use_tiered_pricing') && (
                      <div className="mt-4">
                        <TieredPricingEditor
                          pricingId={pricing.id}
                          accountId={accountId}
                          vehicleId={vehicleId}
                          tiers={pricing.vehicle_pricing_tiers || []}
                          onTiersChange={onPricingChange}
                          onDisableTieredPricing={() => form.setValue('use_tiered_pricing', false)}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Add-ons section */}
                {pricing && (
                  <div className="border-t pt-4">
                    <div className="mb-2 flex items-center justify-between">
                      <Label className="text-sm font-medium">Add-ons</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onEditAddon(pricing.id, null)}
                      >
                        <Plus className="mr-1 h-3 w-3" />
                        Add Add-on
                      </Button>
                    </div>
                    {pricing.vehicle_pricing_addons &&
                    pricing.vehicle_pricing_addons.length > 0 ? (
                      <div className="space-y-1">
                        {pricing.vehicle_pricing_addons.map((addon) => (
                          <div
                            key={addon.id}
                            className="flex items-center justify-between rounded py-1.5 pl-2 pr-1 text-sm hover:bg-muted/50"
                          >
                            <div className="flex items-center gap-2">
                              <span>{addon.name}</span>
                              <span className="text-muted-foreground">
                                +{formatAddonPrice(Number(addon.price), addon.price_type)}
                              </span>
                              {addon.max_quantity > 1 && (
                                <Badge variant="outline" className="text-xs">
                                  x{addon.max_quantity}
                                </Badge>
                              )}
                              {!addon.is_active && (
                                <Badge variant="secondary" className="text-xs">
                                  Inactive
                                </Badge>
                              )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6">
                                  <MoreHorizontal className="h-3 w-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => onEditAddon(pricing.id, addon.id)}
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => onDeleteAddon(addon.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No add-ons configured</p>
                    )}
                  </div>
                )}
              </form>
            </Form>
          ) : (
            <p className="text-sm text-muted-foreground">{disabledMessage}</p>
          )}
        </CardContent>
      </Card>

      {/* Disable confirmation dialog */}
      <AlertDialog open={showDisableConfirm} onOpenChange={setShowDisableConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable {typeLabel} Pricing?</AlertDialogTitle>
            <AlertDialogDescription>
              This pricing option has {pricing?.vehicle_pricing_addons?.length || 0} add-on
              {(pricing?.vehicle_pricing_addons?.length || 0) !== 1 ? 's' : ''} configured.
              Disabling will hide them from the booking flow but not delete them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDisable}>Disable</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  )
}
